import React, { useEffect, useState, useRef } from 'react';
import LeafletMap from '../LeafletMap';
import { getNearbyKoalaPhotos, getPhotoUrlBatch } from '../../services/photoService';
import { Photo } from '../../types/photo';
import { FaCrosshairs } from 'react-icons/fa';
import Lightbox from 'yet-another-react-lightbox';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
dayjs.extend(relativeTime);

const haversineDistance = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number => {
  const R = 6371;
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return +(R * c).toFixed(2);
};

type PhotoWithImage = Photo & {
  imageUrl?: string;
  distanceKmFromUser?: number;
};

const KoalaNearYou = () => {
  const [nearbyPhotos, setNearbyPhotos] = useState<PhotoWithImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState<PhotoWithImage | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const listContainerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);

  const [distanceKm, setDistanceKm] = useState(10);
  const [limit, setLimit] = useState(10);
  const [lat, setLat] = useState(-34.9766);
  const [lng, setLng] = useState(138.6352);

  const fetchNearby = async (latitude: number, longitude: number) => {
    setLoading(true);
    try {
      const data = await getNearbyKoalaPhotos({
        latitude,
        longitude,
        maxDistance: distanceKm * 1000,
        numberLimit: limit,
      });

      const photoIds = data.map(p => p.photoId);
      const urlMap: Record<number, string> = await getPhotoUrlBatch(photoIds);

      const withUrls: PhotoWithImage[] = data.map(photo => {
        const distance = haversineDistance(
          latitude,
          longitude,
          photo.latitudeDecValue,
          photo.longitudeDecValue
        );
        return {
          ...photo,
          imageUrl: urlMap[photo.photoId] || '',
          distanceKmFromUser: distance,
        };
      });

      setNearbyPhotos(withUrls);
    } catch (error) {
      console.error('Failed to fetch nearby koalas:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNearby(lat, lng);
  }, [distanceKm, limit]);

  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const newLat = position.coords.latitude;
        const newLng = position.coords.longitude;
        setLat(newLat);
        setLng(newLng);
        fetchNearby(newLat, newLng);
      },
      (err) => {
        alert("Unable to retrieve your location.");
        console.error(err);
      }
    );
  };

  useEffect(() => {
    itemRefs.current = [];
  }, [nearbyPhotos]);

  useEffect(() => {
    if (selectedPhoto && listContainerRef.current) {
      const idx = nearbyPhotos.findIndex(p => p.photoId === selectedPhoto.photoId);
      const el = itemRefs.current[idx];
      const container = listContainerRef.current;

      if (el && container) {
        const elRect = el.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        const elMiddle = elRect.top + elRect.height / 2;
        const containerMiddle = containerRect.top + containerRect.height / 2;

        const scrollDelta = elMiddle - containerMiddle;

        container.scrollBy({
          top: scrollDelta,
          behavior: 'smooth',
        });
      }
    }
  }, [selectedPhoto, nearbyPhotos]);

  return (
    <section className="py-9">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="flex items-center text-3xl font-bold">Koalas Near You</h2>
        <p className="text-lg text-gray-600 mb-4">
          Explore recent koala sightings near your location.
        </p>
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Map Area */}
          <div className="flex-[2] h-[500px] rounded-xl overflow-hidden border border-gray-200 shadow-md">
            {loading ? (
              <div className="flex items-center justify-center w-full h-full text-gray-400">
                Loading map...
              </div>
            ) : (
              <LeafletMap
                mode="display"
                enableSearch={false}
                lat={lat}
                lng={lng}
                userLocation={null}
                markersFromBackend={nearbyPhotos}
                onMarkerClick={(photo) => setSelectedPhoto(photo)}
                selectedMarker={selectedPhoto}
                zoom={13}
              />
            )}
          </div>

          {/* Sidebar */}
          <div className="flex-[1] flex flex-col max-h-[500px]">
            {/* Controls */}
            <div className="custom-form flex items-center gap-4 mb-2">
              {/* Radius */}
              <div className="field-wrapper">
                <label>Radius (km)</label>
                <input
                  type="text"
                  value={distanceKm}
                  onChange={(e) => setDistanceKm(Number(e.target.value))}
                />
              </div>

              {/* Limit */}
              <div className="field-wrapper">
                <label>Limit</label>
                <input
                  type="text"
                  value={limit}
                  onChange={(e) => setLimit(Number(e.target.value))}
                />
              </div>

              {/* Location Button */}
              <button
                title="Use my location"
                type="button"
                onClick={handleGetLocation}
                className="custom-btn h-10 min-w-[40px] px-3 flex items-center justify-center"
              >
                <FaCrosshairs size={16} />
              </button>
            </div>

            {/* Photo List */}
            <div
              ref={listContainerRef}
              className="flex-1 space-y-2 overflow-y-auto">
              {nearbyPhotos.length === 0 ? (
                <div className="text-center text-gray-500 text-sm py-4">
                  No koala photos found nearby. Try increasing the radius or using your current location.
                </div>
              ) : (
                nearbyPhotos.map((photo, index) => (
                  <div
                    key={photo.photoId}
                    ref={el => {
                      itemRefs.current[index] = el;
                    }}
                    onClick={() => {
                      setSelectedPhoto(photo);
                      setLightboxIndex(index);
                      setLightboxOpen(true);
                    }}
                    className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition ${selectedPhoto?.photoId === photo.photoId ? 'bg-secondary' : 'bg-white'
                      } shadow hover:bg-secondary`}
                  >
                    <img
                      src={photo.imageUrl}
                      alt={photo.title}
                      className="w-16 h-16 object-cover rounded"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold truncate">{photo.title}</div>
                      <div className="text-xs text-gray-400">
                        {dayjs(photo.timestamp).fromNow()}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Lightbox */}
        <Lightbox
          open={lightboxOpen}
          close={() => setLightboxOpen(false)}
          index={lightboxIndex}
          slides={nearbyPhotos.map((p) => ({
            src: p.imageUrl || '',
            alt: p.title || 'Untitled',
          }))}
          plugins={[Thumbnails]}
          thumbnails={{
            position: 'bottom',
            width: 120,
            height: 80,
            padding: 4,
            gap: 8,
            border: 2,
            borderStyle: 'solid',
            borderColor: 'rgba(255, 255, 255, 0.3)',
            borderRadius: 4,
          }}
        />
      </div>
    </section>
  );
};

export default KoalaNearYou;
